import { Injector, inject } from '@angular/core';
import { map, pluck } from 'rxjs/operators';
import {
  GetListParams,
  ListStrategyParams,
  arrToList,
  listStrategyFactory,
  lowerCase,
  paginationSplit,
} from 'wflow-cdk';
import { KindEnum } from 'wflow-variables';
import {
  DocumentTypeCore,
  DocumentTypePermission,
  UserOrgAccountAPIService,
} from '../../core/api';
import { REGISTER_RENDER_MAP } from '../utils/register-render.const';

export function usersDocumentTypeListStrategy<
  R extends Pick<DocumentTypeCore, 'id'> = DocumentTypeCore,
>(params?: ListStrategyParams<DocumentTypeCore, R>, organization?: string) {
  const injector = params?.injector ?? inject(Injector);
  const accountApiSvc = injector.get(UserOrgAccountAPIService);

  return listStrategyFactory(
    (getParams: GetListParams) =>
      accountApiSvc.getAccount(organization).pipe(
        pluck('documentTypes'),
        map((types) =>
          paginationSplit<DocumentTypeCore>(
            arrToList<DocumentTypePermission, DocumentTypeCore>(
              types,
              getParams,
              [searchDocTypeStr, queryFilterStr, pluckDocTypeStr],
              sortTypeList,
            ),
            getParams,
          ),
        ),
      ),
    {
      optionRenderer: (value) =>
        REGISTER_RENDER_MAP.get('documentTypes').call(this, value, injector),
      ...(params ?? {}),
    },
  );
}

function searchDocTypeStr(
  item: DocumentTypePermission,
  params: GetListParams,
): DocumentTypePermission {
  if (
    !item?.documentType?.name ||
    (!!params?.search &&
      !lowerCase(item?.documentType?.name)?.includes(lowerCase(params?.search)))
  ) {
    return null;
  }

  return item;
}

function queryFilterStr(
  item: DocumentTypePermission,
  params: GetListParams,
): DocumentTypePermission {
  if (!params?.query) {
    return item;
  }

  const queries = params?.query?.split(/ and /i) ?? [];
  const keyValQuery = queries.map((q) => {
    const eq = / = /.test(q);
    const [key, value] = q.split(/ = | != /).map((v) => v?.trim());

    return {
      keys: key.split('.'),
      value: value?.replaceAll('"', '') ?? null,
      equal: eq,
    };
  });

  for (const q of keyValQuery) {
    let obj = item;
    for (const k of q.keys) {
      if (obj[k] == null) {
        if (q.value === null && q.equal) {
          continue;
        }
        return null;
      }

      if (typeof obj[k] === 'object') {
        obj = obj[k];
        continue;
      }

      const val = `${obj[k]}`;
      if ((q.value === val && q.equal) || (q.value !== val && !q.equal)) {
        continue;
      }

      return null;
    }
  }

  return item;
}

function pluckDocTypeStr(item: DocumentTypePermission): DocumentTypeCore {
  return item?.documentType;
}

const KINDS = [
  KindEnum.IncomingInvoice,
  KindEnum.OutgoingInvoice,
  KindEnum.ExpenditureCashSlip,
  KindEnum.IncomeCashReceipt,
  KindEnum.SupplierOrder,
  KindEnum.Contract,
  KindEnum.Other,
];

function sortTypeList(list: DocumentTypeCore[]) {
  const map = Object.fromEntries(
    KINDS.map((k) => [k, [] as DocumentTypeCore[]]),
  );

  // Grouped
  for (const type of list) {
    map[type.kind].push(type);
  }

  return KINDS.flatMap((k) =>
    map[k].sort((a, b) => a.name.localeCompare(b.name)),
  );
}
